// An invoice issued through Qonto (sculpt-api PLAN-payments.md M2): read-only, since Qonto numbers it and it changes
// only by a credit note. Shows where the invoicing worker stands, and offers « Relancer » when it failed and « Émettre
// un avoir » once it is issued and not yet credited.
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateOnly, dateTime, money } from '../../services/format';
import { IssueBadge, KindBadge, invoiceNumber, invoiceVat } from '../../components/invoice';
import { Alert, Badge, Button, Card, Confirm, Field, KeyValue, PageTitle, Textarea, statusTone } from '../../components/ui';
import type { InvoiceAdmin } from '../../types/api';

export function QontoInvoice({ invoice, reload }: { invoice: InvoiceAdmin; reload: () => void }) {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const [crediting, setCrediting] = useState(false);
  const [reason, setReason] = useState('');
  // The invoice it credits (a credit note), or the credit note that cancels it (an invoice).
  const { data: related } = useAsync(async () => {
    if (invoice.credits_invoice_id !== null) return { credits: await api.invoice(invoice.credits_invoice_id).catch(() => null), creditedBy: null };
    if (invoice.kind !== 'invoice' || invoice.organization_id === null) return { credits: null, creditedBy: null };
    const page = await api.invoices({ organization_id: invoice.organization_id, page_size: 200 }).catch(() => null);
    return { credits: null, creditedBy: page?.items.find((i) => i.credits_invoice_id === invoice.id) ?? null };
  }, [invoice.id, invoice.credits_invoice_id, invoice.organization_id, invoice.kind]);

  const run = async (name: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(name);
    setFail(null);
    setNotice(null);
    try {
      await fn();
      setNotice(done);
      reload();
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const download = async () => {
    const blob = await api.downloadInvoicePdf(invoice.id);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = invoice.pdf_name ?? `${invoice.number ?? invoice.id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const canCredit = invoice.kind === 'invoice' && invoice.issue_state === 'issued' && !related?.creditedBy;

  return (
    <div>
      <PageTitle
        title={
          <>
            <span className={invoice.number ? '' : 'text-faint'}>{invoiceNumber(invoice, t)}</span> <KindBadge invoice={invoice} />{' '}
            <Badge tone={statusTone(invoice.status)}>{t(`invoices.status.${invoice.status}`)}</Badge> <IssueBadge invoice={invoice} />
          </>
        }
        subtitle={
          invoice.organization_id !== null ? (
            <Link to={`/organizations/${invoice.organization_id}?tab=invoices`} className="text-accent underline">
              {invoice.organization_name}
            </Link>
          ) : (
            invoice.organization_name
          )
        }
        actions={
          <>
            {invoice.has_pdf && <Button onClick={() => void download()}>{t('invoices.downloadPdf')}</Button>}
            {invoice.issue_state === 'failed' && (
              <Button variant="primary" busy={busy === 'retry'} onClick={() => void run('retry', () => api.retryInvoice(invoice.id), t('invoices.retried'))} data-testid="invoice-retry">
                {t('invoices.retry')}
              </Button>
            )}
            {canCredit && (
              <Button variant="danger" onClick={() => setCrediting(true)} data-testid="invoice-credit">
                {t('invoices.creditNote')}
              </Button>
            )}
          </>
        }
      />
      {notice && <div className="mb-3"><Alert tone="good">{notice}</Alert></div>}
      {fail && <div className="mb-3"><Alert>{fail}</Alert></div>}
      {invoice.issue_state === 'failed' && invoice.last_error && (
        <div className="mb-3">
          <Alert>
            {t('invoices.lastError')}: {invoice.last_error}
          </Alert>
        </div>
      )}
      <div className="mb-3">
        <Alert tone="warn">{t('invoices.qontoNote')}</Alert>
      </div>
      {related?.credits && (
        <p className="mb-3 text-[12.5px]">
          <Link to={`/invoices/${related.credits.id}`} className="text-accent underline">
            {t('invoices.credits', { number: invoiceNumber(related.credits, t) })}
          </Link>
        </p>
      )}
      {related?.creditedBy && (
        <p className="mb-3 text-[12.5px]">
          <Link to={`/invoices/${related.creditedBy.id}`} className="text-accent underline" data-testid="credited-by">
            {t('invoices.creditedBy', { number: invoiceNumber(related.creditedBy, t) })}
          </Link>
        </p>
      )}
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Card title={t('invoices.lines')}>
          <table className="w-full text-[12.5px]">
            <tbody>
              {invoice.lines.map((line, i) => (
                <tr key={i} className="border-b border-line-row last:border-0">
                  <td className="py-1.5 pr-3">{line.description}</td>
                  <td className="py-1.5 pr-3 text-right font-mono text-[11.5px]">{String(line.quantity)}</td>
                  <td className="py-1.5 text-right font-mono text-[11.5px]">{money(line.unit_price, invoice.currency, l)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3">
            <KeyValue
              rows={[
                [t('invoices.amountExcl'), <span key="a" className="font-mono">{money(invoice.amount, invoice.currency, l)}</span>],
                [t('invoices.vat'), <span key="v" className="font-mono" data-testid="invoice-vat">{invoiceVat(invoice, t, l)}</span>],
                [t('invoices.total'), <span key="t" className="font-mono" data-testid="invoice-total">{money(invoice.total, invoice.currency, l)}</span>],
                [t('invoices.periodStart'), dateOnly(invoice.period_start, l)],
                [t('invoices.periodEnd'), dateOnly(invoice.period_end, l)],
              ]}
            />
          </div>
        </Card>
        <Card title={t('invoices.lifecycle')}>
          <KeyValue
            rows={[
              [t('invoices.issue.label'), <IssueBadge key="s" invoice={invoice} />],
              [t('invoices.attempts'), String(invoice.attempts)],
              [t('invoices.qontoRef'), <span key="r" className="font-mono text-[11px]">{invoice.provider_ref ?? '—'}</span>],
              [t('invoices.billedTo'), invoice.organization_name],
              [t('invoices.issuedAt'), dateOnly(invoice.issued_at, l)],
              [t('invoices.dueAt'), dateOnly(invoice.due_at, l)],
              [t('invoices.paidAt'), dateTime(invoice.paid_at, l)],
              [t('invoices.sentAt'), dateTime(invoice.sent_at, l)],
              [t('invoices.channel'), invoice.channel ? t(`invoices.channels.${invoice.channel}`) : '—'],
              [t('invoices.pdf'), invoice.has_pdf ? invoice.pdf_name : t('common.no')],
              ...(invoice.order_id !== null && invoice.organization_id !== null
                ? [[t('nav.orders'), <Link key="o" to={`/organizations/${invoice.organization_id}?tab=subscription`} className="text-accent underline">#{invoice.order_id}</Link>] as [string, JSX.Element]]
                : []),
            ]}
          />
        </Card>
      </div>
      <Confirm
        open={crediting}
        danger
        title={t('invoices.creditTitle')}
        message={
          <div className="flex flex-col gap-3">
            <p>{t('invoices.creditMessage')}</p>
            <Field label={t('invoices.creditReason')}>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="credit-reason" />
            </Field>
          </div>
        }
        confirmLabel={t('invoices.creditNote')}
        busy={busy === 'credit'}
        onCancel={() => setCrediting(false)}
        onConfirm={() => {
          if (!reason.trim()) return;
          void run('credit', async () => {
            const note = await api.creditInvoice(invoice.id, reason.trim());
            setCrediting(false);
            navigate(`/invoices/${note.id}`);
          }, t('invoices.creditQueued'));
        }}
      />
    </div>
  );
}
